import { useEffect, useState } from 'react';
import { api, ApiError } from '../lib/api';
import { Crumbs } from '../components/Crumbs';
import { Banner } from '../components/Banner';
import { Skel } from '../components/Skel';
import { EnforcementPanel } from '../components/EnforcementPanel';
import type { EnforcementCheckResponse } from '../data/types';

interface Props {
  /** ИНН из ссылки #/fssp-check?inn= (автопроверка при заходе). */
  initialInn?: string;
}

/**
 * Страница-форма «Проверка по ФССП».
 *  - ИНН юрлица (10 цифр) → исполнительные производства из открытого банка ФССП.
 *  - ИНН физлица/ИП (12 цифр) → редирект на официальный поиск ФССП
 *    (в открытых данных их нет — мы не храним и не парсим персональные данные).
 */
export function FsspCheck({ initialInn }: Props) {
  const [inn, setInn] = useState(initialInn ?? '');
  const [result, setResult] = useState<EnforcementCheckResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isAuthed =
    typeof window !== 'undefined' && !!window.localStorage?.getItem('pcp_access_token');

  const digits = inn.replace(/\D/g, '');
  const valid = digits.length === 10 || digits.length === 12;

  function performCheck(value: string) {
    const d = value.replace(/\D/g, '');
    if (d.length !== 10 && d.length !== 12) return;
    setLoading(true);
    setError(null);
    setResult(null);
    api
      .enforcementCheck(d)
      .then(setResult)
      .catch((e) =>
        setError(
          e instanceof ApiError && e.status === 400
            ? 'Проверьте ИНН: 10 цифр для юрлица или 12 для физлица/ИП.'
            : 'Не удалось выполнить проверку. Попробуйте позже.'
        )
      )
      .finally(() => setLoading(false));
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!valid) return;
    performCheck(digits);
    const next = `#/fssp-check?inn=${digits}`;
    if (window.location.hash !== next) {
      window.history.replaceState(null, '', next);
    }
  }

  // Скрыть серверный SEO-shell после монтирования React (cutover ADR-0015).
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.classList.add('pcp-react-ready');
    }
  }, []);

  // Автопроверка при заходе по прямой ссылке с ИНН.
  useEffect(() => {
    if (initialInn && (initialInn.length === 10 || initialInn.length === 12)) {
      performCheck(initialInn);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <main className="container" style={{ paddingTop: 32 }}>
      <Crumbs items={[{ label: 'Главная', href: '#/' }, { label: 'Проверка по ФССП' }]} />
      <h1
        style={{
          fontFamily: 'Fraunces, Georgia, serif',
          fontSize: 28,
          margin: '8px 0 8px',
          color: 'var(--navy-700, #0b1736)',
        }}
      >
        Проверка по ФССП
      </h1>
      <p style={{ color: 'var(--muted, #6b7287)', marginBottom: 20, maxWidth: 640 }}>
        Исполнительные производства по юридическому лицу из открытого банка данных ФССП России.
        Введите ИНН организации (10 цифр). Для физических лиц и ИП проверка выполняется по ФИО и
        дате рождения на официальном сайте ФССП.
      </p>

      <form
        onSubmit={onSubmit}
        style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 24 }}
      >
        <input
          value={inn}
          onChange={(e) => setInn(e.target.value)}
          inputMode="numeric"
          placeholder="ИНН (10 или 12 цифр)"
          aria-label="ИНН для проверки по ФССП"
          style={{
            flex: '1 1 240px',
            minWidth: 0,
            padding: '12px 14px',
            fontSize: 16,
            border: '1px solid var(--border, #e6e2d6)',
            borderRadius: 10,
            fontFamily: 'JetBrains Mono, ui-monospace, monospace',
          }}
        />
        <button
          type="submit"
          disabled={!valid || loading}
          style={{
            padding: '12px 24px',
            fontSize: 15,
            fontWeight: 600,
            border: 'none',
            borderRadius: 10,
            background: valid && !loading ? 'var(--navy-700, #0b1736)' : '#c8c4b8',
            color: '#fff',
            cursor: valid && !loading ? 'pointer' : 'default',
            flexShrink: 0,
          }}
        >
          {loading ? 'Проверяем…' : 'Проверить'}
        </button>
      </form>

      {loading && <Skel count={4} height={64} />}
      {error && (
        <Banner tone="risk" title="Ошибка">
          {error}
        </Banner>
      )}

      {!loading && result?.kind === 'person' && (
        <div>
          <Banner tone="info" title="Проверка физических лиц и ИП">
            {result.reason}
          </Banner>
          <a
            href={result.redirect_url}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-block',
              marginTop: 12,
              padding: '12px 22px',
              fontSize: 15,
              fontWeight: 600,
              background: 'var(--navy-700, #0b1736)',
              color: '#fff',
              borderRadius: 10,
              textDecoration: 'none',
            }}
          >
            Перейти на официальный сайт ФССП →
          </a>
        </div>
      )}

      {!loading && result?.kind === 'company' && (
        <div>
          {result.result.company_name && (
            <h2
              style={{
                fontFamily: 'Fraunces, Georgia, serif',
                fontSize: 20,
                margin: '0 0 16px',
                overflowWrap: 'anywhere',
              }}
            >
              {result.result.company_name}
              {result.result.slug && (
                <>
                  {' · '}
                  <a
                    href={`#/company/${result.result.slug}`}
                    style={{ fontSize: 14, fontWeight: 400 }}
                  >
                    карточка компании
                  </a>
                </>
              )}
            </h2>
          )}
          <EnforcementPanel data={result.result} isAuthed={isAuthed} />
        </div>
      )}
    </main>
  );
}
